<template>
  <div class="sr-import-progress d-flex align-items-start" style="min-width: 200px;">
    <div class="flex-grow-1">
      <div
        v-if="isLoadingBreakdown"
        class="progress mb-1 sr-progress-clickable"
        style="height: 18px;"
        title="Click for import details"
        @click.stop="openDetail"
      >
        <div
          class="progress-bar progress-bar-striped progress-bar-animated bg-light"
          role="progressbar"
          style="width: 100%;"
        />
      </div>
      <div
        v-else-if="hasBreakdown"
        class="progress mb-1 d-flex sr-progress-clickable position-relative"
        style="height: 18px;"
        :title="breakdownTitle + ' — click for details'"
        @click.stop="openDetail"
      >
        <div
          v-if="loading || refreshing"
          class="progress-bar progress-bar-striped progress-bar-animated bg-info w-100"
          role="progressbar"
          style="opacity: 0.45;"
        />
        <template v-else>
        <div
          v-if="pctImported > 0"
          class="progress-bar bg-success"
          role="progressbar"
          :style="{ width: pctImported + '%' }"
        />
        <div
          v-if="pctQueued > 0"
          class="progress-bar bg-info"
          role="progressbar"
          :style="{ width: pctQueued + '%' }"
        />
        <div
          v-if="pctReady > 0"
          class="progress-bar bg-warning"
          role="progressbar"
          :style="{ width: pctReady + '%' }"
        />
        <div
          v-if="pctSrPending > 0"
          class="progress-bar bg-secondary"
          role="progressbar"
          :style="{ width: pctSrPending + '%' }"
        />
        </template>
      </div>
      <div
        v-else
        class="progress mb-1 sr-progress-clickable"
        style="height: 18px;"
        title="Click for import details"
        @click.stop="openDetail"
      >
        <div
          class="progress-bar"
          :class="progressPct >= 100 ? 'bg-success' : 'bg-primary'"
          role="progressbar"
          :style="{ width: Math.min(100, Math.max(0, progressPct)) + '%' }"
        >
          <small class="text-white font-weight-bold px-1">
            {{ Math.round(progressPct) }}%
          </small>
        </div>
      </div>

      <small
        class="text-muted d-block sr-progress-clickable"
        :title="salesridgeTitle + ' — click for details'"
        @click.stop="openDetail"
      >
        <span v-if="isLoadingBreakdown" class="text-muted">
          <span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
          {{ progressLabel }}
        </span>
        <span v-else-if="loading" class="text-primary">
          <span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
          Enqueueing batches…
        </span>
        <span v-else-if="refreshing" class="text-muted">
          <span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
          Refreshing…
        </span>
        <span v-else>{{ progressLabel }}</span>
      </small>

      <small
        v-if="hasBreakdown && !isLoadingBreakdown && !loading && !refreshing"
        class="d-block text-muted sr-progress-clickable"
        style="font-size: 10px; line-height: 1.3;"
        @click.stop="openDetail"
      >
        <span class="text-success">■</span>
        <template v-if="totalCount > 0 && importedCount < totalCount">
          {{ importedCount }} / {{ totalCount }} imported
        </template>
        <template v-else>
          {{ importedCount }} imported
        </template>
        <template v-if="readyQueued > 0">
          <span class="mx-1">·</span>
          <span class="text-info">■</span> {{ readyQueued }} queued
        </template>
        <template v-if="readyCount > 0">
          <span class="mx-1">·</span>
          <span class="text-warning">■</span> {{ readyCount }} ready
        </template>
        <template v-if="readySrUnlisted > 0">
          <span class="mx-1">·</span>
          <span class="text-secondary">■</span> {{ readySrUnlisted }} on SR (no ID yet)
        </template>
        <template v-if="srPendingCount > 0 && readySrUnlisted === 0">
          <span class="mx-1">·</span>
          <span class="text-secondary">■</span> {{ srPendingCount }} on SR
        </template>
        <template v-else-if="srPendingCount > readySrUnlisted && readySrUnlisted > 0">
          <span class="mx-1">·</span>
          <span class="text-secondary">■</span> {{ srPendingCount - readySrUnlisted }} exporting
        </template>
      </small>

      <small
        v-if="relaunchBlockedHint && !loading && !launching && !refreshing"
        class="d-block text-muted"
        style="font-size: 10px; line-height: 1.35;"
        :title="relaunchBlockedHint"
      >
        {{ relaunchBlockedHint }}
      </small>

      <small
        v-if="message"
        class="d-block"
        :class="messageIsError ? 'text-danger' : 'text-success'"
        :style="messageIsError ? 'white-space: pre-wrap;' : null"
        :title="messageTitle"
      >
        {{ message }}
      </small>
    </div>
    <div
      v-if="row && row.target_id"
      class="d-flex flex-shrink-0 ml-1 align-items-start"
    >
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm py-0 px-1"
        style="line-height: 1.2; font-size: 11px; min-width: 1.6rem;"
        :disabled="refreshing || loading || launching"
        title="Refresh import progress for this order"
        aria-label="Refresh import progress"
        @click.stop="refreshRow"
      >
        <span
          v-if="refreshing"
          class="spinner-border spinner-border-sm"
          role="status"
        >
          <span class="sr-only">Refreshing…</span>
        </span>
        <i
          v-else
          class="icon-refresh"
          aria-hidden="true"
        />
      </button>
      <button
        v-if="showLaunchButton && (canLaunchSalesridge || launching)"
        type="button"
        class="btn btn-outline-success btn-sm py-0 px-1 ml-1"
        style="line-height: 1.2; font-size: 11px; min-width: 1.6rem;"
        :disabled="launching || loading || refreshing"
        :title="launchButtonTitle"
        :aria-busy="launching ? 'true' : 'false'"
        @click.stop="launchSalesridge"
      >
        <span
          v-if="launching"
          class="spinner-border spinner-border-sm"
          role="status"
        >
          <span class="sr-only">Launching…</span>
        </span>
        <i v-else class="icon-rocket" aria-hidden="true" />
      </button>
      <button
        v-if="showEnqueueButton && (canEnqueue || loading)"
        type="button"
        class="btn btn-outline-primary btn-sm py-0 px-1 ml-1"
        style="line-height: 1.2; font-size: 11px; min-width: 1.6rem;"
        :disabled="loading || refreshing || launching"
        :title="loading ? 'Enqueueing batches…' : enqueueButtonTitle"
        :aria-busy="loading ? 'true' : 'false'"
        @click.stop="enqueue"
      >
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm"
          role="status"
        >
          <span class="sr-only">Enqueueing…</span>
        </span>
        <i v-else class="icon-arrow-circle-o-right" aria-hidden="true" />
      </button>
    </div>

    <Modal :name="modalName" :size="'70'" :scrollable="true">
      <div class="p-4">
        <h5 class="mb-1">
          SalesRidge import detail
          <small v-if="detail && detail.target_name" class="text-muted d-block font-weight-normal">
            {{ detail.target_name }}
            <span class="text-muted">#{{ detail.target_id }}</span>
          </small>
        </h5>

        <div v-if="detailLoading" class="text-center py-4">
          <span class="spinner-border" role="status" aria-hidden="true" />
          <p class="text-muted small mt-2 mb-0">Loading batch details &amp; SalesRidge API…</p>
        </div>

        <div v-else-if="detailError" class="alert alert-danger small mb-0">
          {{ detailError }}
        </div>

        <div v-else-if="detail">
          <div
            v-if="detail.no_salesridge"
            class="alert small mb-3"
            :class="detailAlertClass"
          >
            <strong>{{ detail.status_message }}</strong>
            <span
              v-if="detail.sr_relaunch_blocked_reason"
              class="d-block mt-1 mb-0"
            >
              {{ detail.sr_relaunch_blocked_reason }}
            </span>
            <span
              v-if="detail.salesridge_launch_error"
              class="d-block mt-1 mb-0 text-danger"
            >
              Last launch error: {{ detail.salesridge_launch_error }}
            </span>
            <span
              v-if="Number(detail.sr_relaunch_allowed) === 1"
              class="d-block mt-2 mb-0"
            >
              Use the <strong>Launch</strong> button (rocket) on the row to run the SalesRidge search.
            </span>
          </div>

          <div class="mb-3">
            <strong>salesridge_id</strong>
            <code class="d-block mt-1">{{ detail.salesridge_id || "—" }}</code>
            <small v-if="detail.salesridge_launched_label" class="text-muted">
              Search launched: {{ detail.salesridge_launched_label }}
            </small>
          </div>

          <div v-if="detail.breakdown" class="mb-3 small">
            <strong>Breakdown</strong>
            <span class="d-block text-muted mt-1">
              <span class="text-success">
                <template v-if="detail.breakdown.total > 0 && detail.breakdown.imported < detail.breakdown.total">
                  {{ detail.breakdown.imported }} / {{ detail.breakdown.total }}
                </template>
                <template v-else>
                  {{ detail.breakdown.imported }}
                </template>
              </span> imported in Oliverlist
              <span v-if="detail.breakdown.exported_not_local > 0" class="text-secondary">
                · {{ detail.breakdown.exported_not_local }} exported on SR, not in DB yet
              </span>
              <span v-if="detail.breakdown.ready_queued > 0" class="text-info">
                {{ detail.breakdown.ready_queued }} queued
              </span>
              <span v-if="detail.breakdown.ready_queued > 0"> · </span>
              <span class="text-warning">{{ detail.breakdown.ready_oliverlist }}</span> ready (API)
              <span v-if="detail.breakdown.ready_sr_unlisted > 0" class="text-muted">
                · {{ detail.breakdown.ready_sr_unlisted }} reported by SR (no batch ID yet)
              </span>
              ·
              <span class="text-secondary">{{ detail.breakdown.sr_pending }}</span> on SR
              <span v-if="detail.breakdown.total > 0">
                / {{ detail.breakdown.total }} total
              </span>
            </span>
            <span
              v-if="detail.breakdown.exported_sr != null || detail.breakdown.ready_sr != null"
              class="d-block text-muted mt-1"
            >
              SalesRidge getOrderStats:
              {{ detail.breakdown.exported_sr }} exported ·
              {{ detail.breakdown.ready_sr }} ready for Oliverlist ·
              {{ detail.breakdown.sr_pending }} still exporting on SR
            </span>
            <span
              v-if="detail.breakdown.ready_sr > detail.breakdown.ready_oliverlist"
              class="d-block text-muted mt-1"
            >
              SalesRidge getOrderStats reports {{ detail.breakdown.ready_sr }} ready overall.
              Yellow shows only {{ detail.breakdown.ready_oliverlist }} with a confirmed batch ID
              in listApiExportBatches (same as Enqueue).
            </span>
          </div>

          <h6 class="mt-3 mb-2">
            Pending in salesridge_batch_pending
            <span class="text-muted font-weight-normal">({{ detail.pending_batches.length }})</span>
          </h6>
          <p v-if="!detail.pending_batches.length" class="text-muted small">
            No batches queued for this order right now.
          </p>
          <div v-else class="table-responsive mb-3">
            <table class="table table-sm table-bordered small mb-0">
              <thead class="thead-light">
                <tr>
                  <th>Worker</th>
                  <th>batch_id</th>
                  <th>salesridge_id</th>
                  <th>Queued at</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(b, i) in detail.pending_batches" :key="'p-' + i">
                  <td>w{{ b.position }}</td>
                  <td><code>{{ b.batch_id }}</code></td>
                  <td><code>{{ b.salesridge_id }}</code></td>
                  <td>
                    {{ b.queued_at_label || b.queued_at || "—" }}
                    <span v-if="b.queued_ago" class="text-muted">({{ b.queued_ago }})</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h6 class="mb-2">
            Imported in salesridge_batch
            <span class="text-muted font-weight-normal">({{ detail.imported_batches.length }} shown)</span>
          </h6>
          <p v-if="!detail.imported_batches.length" class="text-muted small">
            No local batches imported yet for this salesridge_id.
          </p>
          <div v-else class="table-responsive mb-3">
            <table class="table table-sm table-bordered small mb-0">
              <thead class="thead-light">
                <tr>
                  <th>batch_id</th>
                  <th>salesridge_id</th>
                  <th>Imported at</th>
                  <th>valid</th>
                  <th>total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(b, i) in detail.imported_batches" :key="'i-' + i">
                  <td><code>{{ b.batch_id }}</code></td>
                  <td><code>{{ b.salesridge_id }}</code></td>
                  <td>
                    {{ b.imported_at_label || b.imported_at || "—" }}
                    <span v-if="b.imported_ago" class="text-muted">({{ b.imported_ago }})</span>
                  </td>
                  <td>{{ b.valid }}</td>
                  <td>{{ b.total }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h6 class="mt-3 mb-2">
            Ready on SalesRidge
            <span class="text-muted font-weight-normal">
              ({{ detail.ready_batches_total || 0 }} total · yellow bar)
            </span>
          </h6>
          <p v-if="detail.on_sr_batches_error" class="text-danger small mb-2">
            {{ detail.on_sr_batches_error }}
          </p>
          <p v-else-if="!(detail.ready_batches_total > 0)" class="text-muted small">
            No ready_for_export batches waiting (not imported / not queued).
          </p>
          <div v-else-if="detail.ready_batches && detail.ready_batches.length" class="table-responsive mb-3">
            <table class="table table-sm table-bordered small mb-0">
              <thead class="thead-light">
                <tr>
                  <th>batch_id</th>
                  <th>SR status</th>
                  <th>leads</th>
                  <th>updated (SR)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(b, i) in detail.ready_batches" :key="'r-' + i" class="table-warning">
                  <td><code>{{ b.batch_id }}</code></td>
                  <td><span class="badge badge-warning text-dark">{{ b.status }}</span></td>
                  <td>{{ b.lead_count != null ? b.lead_count : "—" }}</td>
                  <td>{{ b.updated_at || "—" }}</td>
                </tr>
              </tbody>
            </table>
            <p
              v-if="detail.ready_batches_total > detail.ready_batches.length"
              class="text-muted small mb-0 mt-1"
            >
              Showing {{ detail.ready_batches.length }} of {{ detail.ready_batches_total }} ready batch(es).
            </p>
          </div>

          <h6 class="mb-2">
            Still on SalesRidge
            <span class="text-muted font-weight-normal">
              ({{ detail.on_sr_batches_total || 0 }} listed · grey bar)
            </span>
          </h6>
          <p v-if="detail.on_sr_status_counts && Object.keys(detail.on_sr_status_counts).length" class="small text-muted mb-2">
            SR statuses:
            {{ formatStatusCounts(detail.on_sr_status_counts) }}
            <span v-if="detail.on_sr_unlisted > 0">
              · {{ detail.on_sr_unlisted }} more not returned by listApiExportBatches
            </span>
          </p>
          <p v-else-if="detail.on_sr_unlisted > 0" class="text-muted small mb-2">
            {{ detail.on_sr_unlisted }} batch(es) still on SR but not returned by listApiExportBatches
            (exporting / no batch ID yet).
          </p>
          <p v-else-if="!(detail.on_sr_batches_total > 0)" class="text-muted small">
            No non-ready batches listed for this order in SalesRidge API.
          </p>
          <div v-else-if="detail.on_sr_batches && detail.on_sr_batches.length" class="table-responsive mb-3">
            <table class="table table-sm table-bordered small mb-0">
              <thead class="thead-light">
                <tr>
                  <th>batch_id</th>
                  <th>SR status</th>
                  <th>leads</th>
                  <th>source</th>
                  <th>updated (SR)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(b, i) in detail.on_sr_batches" :key="'g-' + i" class="table-secondary">
                  <td><code>{{ b.batch_id }}</code></td>
                  <td><span class="badge badge-secondary">{{ b.status }}</span></td>
                  <td>{{ b.lead_count != null ? b.lead_count : "—" }}</td>
                  <td>{{ b.source || "—" }}</td>
                  <td>{{ b.updated_at || "—" }}</td>
                </tr>
              </tbody>
            </table>
            <p
              v-if="detail.on_sr_batches_total > detail.on_sr_batches.length"
              class="text-muted small mb-0 mt-1"
            >
              Showing {{ detail.on_sr_batches.length }} of {{ detail.on_sr_batches_total }} on-SR batch(es).
            </p>
          </div>

          <div v-if="detail.db_summary" class="mb-3">
            <h6 class="mb-2">Local DB summary</h6>
            <pre class="small bg-light p-2 mb-0" style="white-space: pre-wrap;">{{ detail.db_summary.import_batch_detail }}</pre>
          </div>

          <div v-if="detail.api && !detail.no_salesridge" class="mb-2">
            <h6 class="mb-2">SalesRidge API snapshot</h6>
            <p class="small text-muted mb-2">
              {{ detail.api.note }}
              <span v-if="detail.api.fetched_at"> Fetched: {{ detail.api.fetched_at }}.</span>
              <strong v-if="detail.api.stored_in_db === false" class="d-block">
                Not stored in Oliverlist DB — live only.
              </strong>
            </p>

            <p class="small mb-1"><strong>getOrderStats</strong></p>
            <pre class="small bg-light p-2 mb-3" style="max-height: 180px; overflow: auto;">{{ formatJson(detail.api.get_order_stats) }}</pre>

            <template v-if="detail.api.list_export_batches">
              <p class="small mb-1"><strong>listApiExportBatches</strong></p>
              <div v-if="!detail.api.list_export_batches.ok" class="alert alert-warning small py-2">
                {{ detail.api.list_export_batches.error }}
              </div>
              <div v-else class="small mb-2">
                <span v-if="detail.api.list_export_batches.task_status">
                  task: {{ detail.api.list_export_batches.task_status }}
                </span>
                <span v-if="detail.api.list_export_batches.message" class="d-block text-muted">
                  {{ detail.api.list_export_batches.message }}
                </span>
                <span class="d-block">
                  {{ detail.api.list_export_batches.batches_listed }} batches listed ·
                  statuses: {{ formatStatusCounts(detail.api.list_export_batches.status_counts) }}
                </span>
              </div>
              <pre
                class="small bg-light p-2 mb-0"
                style="max-height: 220px; overflow: auto;"
              >{{ formatJson(detail.api.list_export_batches.raw_response) }}</pre>
            </template>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { targetsService } from "@/_services";
import Modal from "@/components/Common/Modal.vue";
import {
  formatSalesridgeRelaunchError,
  salesridgeLaunchButtonLabel,
} from "@/_helpers/salesridgeLaunchUi";

export default {
  name: "SalesridgeImportProgressCell",
  components: {
    Modal,
  },
  props: {
    row: {
      type: Object,
      required: true,
    },
    showEnqueueButton: {
      type: Boolean,
      default: false,
    },
    showLaunchButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      launching: false,
      refreshing: false,
      message: "",
      messageTitle: "",
      messageIsError: false,
      detailLoading: false,
      detailError: "",
      detail: null,
    };
  },
  computed: {
    modalName() {
      const id = this.row && this.row.target_id;
      return id ? `sr-import-detail-${id}` : "sr-import-detail";
    },
    detailAlertClass() {
      const tone = this.detail && this.detail.import_progress_row_tone;
      if (tone === "no_salesridge_blocked") {
        return "alert-warning";
      }
      if (tone === "no_salesridge_running") {
        return "alert-success";
      }
      if (tone === "warmup") {
        return "alert-secondary";
      }
      return "alert-info";
    },
    isLoadingBreakdown() {
      return Number(this.row.sr_import_breakdown_loading) === 1;
    },
    hasBreakdown() {
      return Number(this.row.sr_import_breakdown_total) > 0;
    },
    totalCount() {
      return Number(this.row.sr_import_breakdown_total)
        || Number(this.row.sr_import_total_batches)
        || 0;
    },
    importedCount() {
      if (this.row.sr_import_breakdown_imported != null) {
        return Number(this.row.sr_import_breakdown_imported) || 0;
      }
      return Number(this.row.sr_batches_imported) || 0;
    },
    readyCount() {
      if (this.row.sr_import_breakdown_ready != null) {
        return Number(this.row.sr_import_breakdown_ready) || 0;
      }
      return Number(this.row.sr_import_ready_batches) || 0;
    },
    readyQueued() {
      return Number(this.row.sr_import_breakdown_ready_queued) || 0;
    },
    readyEnqueueable() {
      return Number(this.row.sr_import_breakdown_ready_enqueueable) || 0;
    },
    readySrUnlisted() {
      return Number(this.row.sr_import_breakdown_ready_sr_unlisted) || 0;
    },
    srPendingCount() {
      return Number(this.row.sr_import_breakdown_sr_pending) || 0;
    },
    pctImported() {
      if (this.row.sr_import_breakdown_pct_imported != null) {
        return Number(this.row.sr_import_breakdown_pct_imported) || 0;
      }
      if (this.totalCount > 0) {
        return Math.round((this.importedCount / this.totalCount) * 1000) / 10;
      }
      return 0;
    },
    pctQueued() {
      return Number(this.row.sr_import_breakdown_pct_queued) || 0;
    },
    pctReady() {
      return Number(this.row.sr_import_breakdown_pct_ready) || 0;
    },
    pctSrPending() {
      return Number(this.row.sr_import_breakdown_pct_sr_pending) || 0;
    },
    progressPct() {
      return Number(this.row.sr_import_progress_pct) || 0;
    },
    progressLabel() {
      if (this.row.sr_import_progress_label) {
        return this.row.sr_import_progress_label;
      }
      if (this.totalCount > 0) {
        return `${this.importedCount} / ${this.totalCount} batches`;
      }
      return "—";
    },
    breakdownTitle() {
      if (!this.hasBreakdown) {
        return "";
      }
      const importedLabel =
        this.totalCount > 0 && this.importedCount < this.totalCount
          ? `${this.importedCount} / ${this.totalCount} imported`
          : `${this.importedCount} imported`;
      const parts = [importedLabel];
      if (this.readyQueued > 0) {
        parts.push(`${this.readyQueued} queued (import.php)`);
      }
      if (this.readyCount > 0) {
        parts.push(`${this.readyCount} ready (API-confirmed)`);
      }
      if (this.readySrUnlisted > 0) {
        parts.push(`${this.readySrUnlisted} on SR (no batch ID yet)`);
      } else if (this.srPendingCount > 0) {
        parts.push(`${this.srPendingCount} on SalesRidge`);
      }
      return `${parts.join(" · ")} (${this.totalCount} total)`;
    },
    salesridgeTitle() {
      return this.row.salesridge_id ? `#${this.row.salesridge_id}` : "";
    },
    canEnqueue() {
      if (this.isLoadingBreakdown || this.loading || this.launching || this.refreshing || !this.hasBreakdown) {
        return false;
      }
      return (Number(this.row.sr_import_breakdown_ready_enqueueable) || 0) > 0;
    },
    canLaunchSalesridge() {
      if (!this.showLaunchButton || this.loading || this.launching || this.refreshing) {
        return false;
      }
      if (Number(this.row.sr_relaunch_allowed) === 0) {
        return false;
      }
      if (this.row.import_progress_row_tone === "no_salesridge") {
        return true;
      }
      const salesridgeId = String(this.row.salesridge_id || "").trim();
      return salesridgeId === "";
    },
    relaunchBlockedHint() {
      if (Number(this.row.sr_relaunch_allowed) !== 0) {
        return "";
      }
      const reason = String(this.row.sr_relaunch_blocked_reason || "").trim();
      if (reason) {
        return reason;
      }
      return "Relaunch not available for this order.";
    },
    launchButtonTitle() {
      const ui = this.row.salesridge_launch_ui;
      if (ui) {
        return salesridgeLaunchButtonLabel(ui);
      }
      return this.$t("lang.search.filter_relaunch_salesridge_button");
    },
    enqueueButtonTitle() {
      const enqueueable = Number(this.row.sr_import_breakdown_ready_enqueueable) || 0;
      if (enqueueable > 0) {
        return `Enqueue ${enqueueable} batch(es) with confirmed ready_for_export IDs`;
      }
      return "No batches with confirmed ready_for_export IDs to enqueue";
    },
  },
  methods: {
    openDetail() {
      const targetId = this.row && this.row.target_id;
      if (!targetId) {
        return;
      }
      this.detailError = "";
      this.detail = null;
      this.detailLoading = true;
      this.$modal.show(this.modalName);

      targetsService
        .getTargetImportProgressDetail(targetId)
        .then((res) => {
          this.detail = res;
        })
        .catch((err) => {
          const msg =
            (err && typeof err === "object" && err.message)
            || (typeof err === "string" ? err : "");
          this.detailError = msg || "Could not load import detail.";
        })
        .finally(() => {
          this.detailLoading = false;
        });
    },
    formatJson(value) {
      if (value == null) {
        return "—";
      }
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return String(value);
      }
    },
    formatStatusCounts(counts) {
      if (!counts || typeof counts !== "object") {
        return "—";
      }
      return Object.keys(counts)
        .map((k) => `${k} (${counts[k]})`)
        .join(", ");
    },
    async refreshRow() {
      const targetId = this.row && this.row.target_id;
      if (!targetId || this.refreshing || this.loading) {
        return;
      }

      this.refreshing = true;
      this.message = "";
      this.messageTitle = "";
      this.messageIsError = false;

      try {
        const refreshed = await targetsService.getTargetSalesridgeImportProgressRow(targetId);
        if (refreshed) {
          this.$emit("row-updated", {
            target_id: targetId,
            row: refreshed,
          });
        }
      } catch (err) {
        const errMessage =
          (err && typeof err === "object" && err.message)
          || (typeof err === "string" ? err : "");
        this.message = errMessage || "Could not refresh import progress.";
        this.messageIsError = true;
      } finally {
        this.refreshing = false;
      }
    },
    async launchSalesridge() {
      const targetId = this.row && this.row.target_id;
      if (!targetId || this.launching || !this.canLaunchSalesridge) {
        return;
      }

      const confirmMsg = this.launchButtonTitle
        + "?\n\nThis runs leadsSearch for this order. Import continues on the usual cron schedule.";
      if (!window.confirm(confirmMsg)) {
        return;
      }

      this.launching = true;
      this.message = "";
      this.messageTitle = "";
      this.messageIsError = false;

      try {
        const res = await targetsService.relaunchSalesridgeSearch(targetId);
        this.message = (res && res.message)
          ? String(res.message)
          : this.$t("lang.search.filter_relaunch_salesridge_success");
        this.messageIsError = false;

        try {
          const refreshed = await targetsService.getTargetSalesridgeImportProgressRow(targetId);
          if (refreshed) {
            this.$emit("row-updated", {
              target_id: targetId,
              row: refreshed,
            });
          }
        } catch (refreshErr) {
          // Launch succeeded; row refresh is best-effort.
        }
      } catch (err) {
        this.message = formatSalesridgeRelaunchError(this.$i18n, err);
        this.messageIsError = true;
      } finally {
        this.launching = false;
      }
    },
    async enqueue() {
      const targetId = this.row && this.row.target_id;
      if (!targetId || this.loading || !this.canEnqueue) {
        return;
      }

      this.loading = true;
      this.message = "";
      this.messageTitle = "";
      this.messageIsError = false;

      try {
        const res = await targetsService.enqueueSalesridgeImportBatches(targetId);
        const enqueued = Number(res && res.enqueued) || 0;
        this.message = (res && res.message) || (enqueued > 0 ? "Batches enqueued." : "Nothing to enqueue.");
        this.messageTitle = this.buildEnqueueMessageTitle(res);
        this.messageIsError = enqueued === 0;

        if (res && res.pending_total != null) {
          this.$emit("pipeline-updated", {
            pending_total: res.pending_total,
            is_importing_now: res.pending_total > 0,
          });
        }

        try {
          const refreshed = await targetsService.getTargetSalesridgeImportProgressRow(targetId);
          if (refreshed) {
            this.$emit("row-updated", {
              target_id: targetId,
              row: refreshed,
            });
          }
        } catch (refreshErr) {
          // Enqueue succeeded; row refresh is best-effort only.
        }
      } catch (err) {
        const errMessage =
          (err && typeof err === "object" && err.message)
          || (typeof err === "string" ? err : "");
        this.message = errMessage || "Could not enqueue batches.";
        this.messageTitle = "";
        this.messageIsError = true;
      } finally {
        this.loading = false;
      }
    },
    buildEnqueueMessageTitle(res) {
      if (!res || !res.enqueue_diagnosis) {
        return res && res.reason ? `Reason: ${res.reason}` : "";
      }
      const d = res.enqueue_diagnosis;
      const parts = [];
      if (res.reason) {
        parts.push(`Reason: ${res.reason}`);
      }
      if (d.api_batches_total != null) {
        parts.push(`SR batches listed: ${d.api_batches_total}`);
      }
      if (d.ready_for_export != null) {
        parts.push(`ready_for_export: ${d.ready_for_export}`);
      }
      if (d.enqueueable != null) {
        parts.push(`enqueueable: ${d.enqueueable}`);
      }
      if (d.handled_ready != null) {
        parts.push(`already handled: ${d.handled_ready}`);
      }
      if (d.already_in_queue != null) {
        parts.push(`in salesridge_batch_pending: ${d.already_in_queue}`);
      }
      if (d.already_imported != null) {
        parts.push(`in salesridge_batch: ${d.already_imported}`);
      }
      if (d.local_pending_for_order != null) {
        parts.push(`pending rows (this order): ${d.local_pending_for_order}`);
      }
      if (d.local_imported_for_order != null) {
        parts.push(`imported rows (this order): ${d.local_imported_for_order}`);
      }
      if (d.task_status) {
        parts.push(`task: ${d.task_status}`);
      }
      return parts.join(" · ");
    },
  },
};
</script>

<style scoped>
.sr-progress-clickable {
  cursor: pointer;
}
.sr-progress-clickable:hover {
  opacity: 0.92;
}
</style>
